import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Copy, Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { type AdminUserDTO, type ProviderDTO } from '@harness/shared';
import { Modal, PageHeader, Spinner, SwitchRow } from '../../components/ui';
import { api, errorText } from '../../lib/api';
import { relTime, usd } from '../../lib/format';
import { useProviders } from '../../lib/queries';
import { toast } from '../../lib/store';
import ProvidersPage, { adminKeyScope } from '../settings/ProvidersPage';

const usersKey = ['admin', 'users'] as const;

function signIn(u: AdminUserDTO) {
  if (!u.lastLoginAt) return 'Not signed in yet';
  return [u.hasSso && 'SSO', u.hasPassword && 'Password'].filter(Boolean).join(' + ') || 'Signed in';
}

export default function PeoplePage() {
  const nav = useNavigate();
  const users = useQuery({ queryKey: usersKey, queryFn: () => api.get<AdminUserDTO[]>('/api/admin/users') });
  const [adding, setAdding] = useState(false);
  const [q, setQ] = useState('');
  const rows = (users.data ?? []).filter((u) => !q || `${u.name} ${u.email}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="settings-content">
      <PageHeader title="People" sub="Set people up before they sign in: their keys are ready when they arrive (matched by email when they sign in with SSO).">
        <button className="btn btn-primary" onClick={() => setAdding(true)}>
          <Plus className="icon" />
          Add person
        </button>
      </PageHeader>
      <input className="input" placeholder="Filter by name or email" value={q} onChange={(e) => setQ(e.target.value)} style={{ maxWidth: 320, marginBottom: 12 }} aria-label="Filter people" />
      {users.isLoading ? (
        <Spinner />
      ) : (
        <section className="card audit-card">
          <table className="table">
            <thead>
              <tr>
                <th>Person</th>
                <th style={{ width: 110 }}>Access</th>
                <th style={{ width: 170 }}>Sign-in</th>
                <th style={{ width: 80 }}>Projects</th>
                <th style={{ width: 70 }}>Keys</th>
                <th style={{ width: 110 }}>Spend (30 d)</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((u) => (
                <tr key={u.id} style={{ cursor: 'pointer' }} onClick={() => nav(`/admin/people/${u.id}`)}>
                  <td>
                    <b>{u.name}</b>
                    {u.you && <span className="tag tag-accent sm" style={{ marginLeft: 8 }}>You</span>}
                    {u.disabled && <span className="tag sm" style={{ marginLeft: 8 }}>Disabled</span>}
                    <div className="muted" style={{ fontSize: 12 }}>
                      {u.email}
                    </div>
                  </td>
                  <td>{u.isAdmin ? <span className="tag tag-accent sm">Admin</span> : 'Member'}</td>
                  <td style={{ fontSize: 13 }}>
                    {signIn(u)}
                    {u.lastLoginAt && (
                      <div className="muted" style={{ fontSize: 12 }}>
                        {relTime(u.lastLoginAt)}
                      </div>
                    )}
                  </td>
                  <td>{u.projectCount}</td>
                  <td className={u.providerCount ? undefined : 'faint'}>{u.providerCount || '—'}</td>
                  <td>
                    {usd(u.usage30d.costUsd)}
                    {u.usage30d.unpricedTokens > 0 && <span className="faint"> +?</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
      {adding && <AddPersonDialog onClose={() => setAdding(false)} onAdded={(u) => nav(`/admin/people/${u.id}`)} />}
    </div>
  );
}

function AddPersonDialog({ onClose, onAdded }: { onClose: () => void; onAdded: (u: AdminUserDTO) => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const u = await api.post<AdminUserDTO>('/api/admin/users', { name: name.trim(), email: email.trim(), isAdmin, password: password || undefined });
      await qc.invalidateQueries({ queryKey: usersKey });
      toast(`${u.name} added · now give them provider keys`);
      onAdded(u);
    } catch (e) {
      toast(errorText(e), 'error');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal open onClose={onClose} label="Add person" width={480}>
      <div className="dialog-title">Add person</div>
      <div className="dialog-body">They can sign in with SSO using this email, and everything you set up is waiting for them.</div>
      <div className="stack-sm">
        <div className="field">
          <label htmlFor="ap-name">Name</label>
          <input id="ap-name" className="input" autoFocus value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="ap-email">Email</label>
          <input id="ap-email" className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@reciteme.com" />
        </div>
        <div className="field">
          <label htmlFor="ap-pw">Password (optional)</label>
          <input id="ap-pw" className="input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Only if password sign-in is on · 10+ characters" />
        </div>
        <SwitchRow on={isAdmin} onChange={setIsAdmin}>
          Admin: can manage people, keys and the agent library
        </SwitchRow>
      </div>
      <div className="dialog-actions">
        <button className="btn btn-ghost" onClick={onClose}>
          Cancel
        </button>
        <button className="btn btn-primary" disabled={busy || !name.trim() || !email.includes('@') || (!!password && password.length < 10)} onClick={() => void save()}>
          Add person
        </button>
      </div>
    </Modal>
  );
}

export function PersonPage({ userId }: { userId: string }) {
  const qc = useQueryClient();
  const nav = useNavigate();
  const key = ['admin', 'user', userId];
  const user = useQuery({ queryKey: key, queryFn: () => api.get<AdminUserDTO>(`/api/admin/users/${userId}`) });
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [copying, setCopying] = useState(false);
  useEffect(() => {
    if (user.data) {
      setName(user.data.name);
      setEmail(user.data.email);
    }
  }, [user.data?.name, user.data?.email]);

  if (user.isLoading) return <Spinner />;
  if (!user.data) return <div className="banner">{errorText(user.error)}</div>;
  const u = user.data;

  const patch = async (body: Record<string, unknown>, done: string) => {
    try {
      qc.setQueryData(key, await api.patch<AdminUserDTO>(`/api/admin/users/${userId}`, body));
      void qc.invalidateQueries({ queryKey: usersKey });
      toast(done);
    } catch (e) {
      toast(errorText(e), 'error');
    }
  };

  return (
    <div className="settings-content">
      <Link to="/admin/people" className="muted" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, marginBottom: 8 }}>
        <ArrowLeft className="icon" /> People
      </Link>
      <PageHeader
        title={u.name}
        sub={`${u.email} · ${u.isAdmin ? 'Admin' : 'Member'} · ${u.projectCount} ${u.projectCount === 1 ? 'project' : 'projects'} · ${usd(u.usage30d.costUsd)} in the last 30 days${u.disabled ? ' · disabled' : ''}`}
      />

      <div className="grid-2" style={{ alignItems: 'start' }}>
        <section className="card card-pad stack-sm">
          <div className="section-title">Details</div>
          <div className="field">
            <label htmlFor="pp-name">Name</label>
            <input id="pp-name" className="input" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="pp-email">Email</label>
            <input id="pp-email" className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            <div className="hint">SSO sign-in finds their account by this email.</div>
          </div>
          <div>
            <button
              className="btn btn-secondary"
              disabled={!name.trim() || !email.includes('@') || (name.trim() === u.name && email.trim().toLowerCase() === u.email)}
              onClick={() => void patch({ name: name.trim(), email: email.trim() }, 'Details saved')}
            >
              Save details
            </button>
          </div>
          <div className="muted" style={{ fontSize: 12 }}>
            User id {u.id} · their local projects live in projects/{u.id}/
          </div>
        </section>

        <section className="card card-pad stack-sm">
          <div className="section-title">Access</div>
          <SwitchRow on={u.isAdmin} onChange={(v) => void patch({ isAdmin: v }, v ? `${u.name} is now an admin` : `${u.name} is no longer an admin`)}>
            Admin: manages people, keys and the agent library (never sees others' projects)
          </SwitchRow>
          <SwitchRow on={!u.disabled} onChange={(v) => void patch({ disabled: !v }, v ? `${u.name} re-enabled` : `${u.name} disabled and signed out`)} disabled={u.you}>
            Account enabled{u.you ? ' (you can’t disable yourself)' : ''}
          </SwitchRow>
          <div className="muted" style={{ fontSize: 13 }}>
            {signIn(u)}
            {u.lastLoginAt ? ` · last ${relTime(u.lastLoginAt)}` : ''} · two-step {u.mfaEnabled ? 'on' : 'off'}
          </div>
          <div className="field">
            <label htmlFor="pp-pw">Password</label>
            <div className="row" style={{ flexWrap: 'nowrap' }}>
              <input id="pp-pw" className="input" type="password" placeholder={u.hasPassword ? 'Set a new password' : 'No password (SSO only)'} value={password} onChange={(e) => setPassword(e.target.value)} />
              <button className="btn btn-secondary" disabled={password.length < 10} onClick={() => void patch({ password }, 'Password set').then(() => setPassword(''))}>
                Set
              </button>
              {u.hasPassword && (
                <button className="btn btn-ghost" onClick={() => void patch({ password: null }, 'Password removed')}>
                  Remove
                </button>
              )}
            </div>
            <div className="hint">Only used when password sign-in is on (PASSWORD_LOGIN). 10+ characters.</div>
          </div>
        </section>
      </div>

      <section className="card card-pad stack-sm" style={{ marginTop: 16 }}>
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <div>
            <div className="section-title">Provider keys</div>
            <div className="muted" style={{ fontSize: 13 }}>
              Keys {u.name} can use. They can also add or change their own. Keys are encrypted and never shown again.
            </div>
          </div>
          <button className="btn btn-secondary" onClick={() => setCopying(true)}>
            <Copy className="icon" />
            Copy one of my providers
          </button>
        </div>
      </section>
      <div style={{ marginTop: 12 }}>
        <ProvidersPage scope={adminKeyScope(u.id, u.name)} />
      </div>

      {!u.you && (
        <section className="card card-pad" style={{ marginTop: 16 }}>
          <div className="section-title">Danger zone</div>
          <div className="row" style={{ justifyContent: 'space-between' }}>
            <div className="muted" style={{ fontSize: 13 }}>
              Deleting {u.name} also deletes their {u.projectCount} {u.projectCount === 1 ? 'project' : 'projects'}, conversations and keys. Disable them instead to keep their work.
            </div>
            <button className="btn btn-secondary" onClick={() => setDeleting(true)}>
              Delete person
            </button>
          </div>
        </section>
      )}
      {deleting && <DeleteDialog user={u} onClose={() => setDeleting(false)} onDeleted={() => nav('/admin/people')} />}
      {copying && <CopyProviderDialog user={u} onClose={() => setCopying(false)} />}
    </div>
  );
}

function CopyProviderDialog({ user, onClose }: { user: AdminUserDTO; onClose: () => void }) {
  const qc = useQueryClient();
  const { data: mine = [] } = useProviders();
  const [pick, setPick] = useState('');
  const scope = adminKeyScope(user.id, user.name);
  const copy = async () => {
    try {
      const p = await api.post<ProviderDTO>(`${scope.base}/providers`, { copyFromProviderId: pick });
      await qc.invalidateQueries({ queryKey: scope.keys.providers });
      void qc.invalidateQueries({ queryKey: usersKey });
      toast(`${p.name} copied to ${user.name} with its ${p.models.length} models`);
      onClose();
    } catch (e) {
      toast(errorText(e), 'error');
    }
  };
  return (
    <Modal open onClose={onClose} label="Copy a provider">
      <div className="dialog-title">Copy a provider to {user.name}</div>
      <div className="dialog-body">They get their own copy: same base URL, key, models and prices. Changing it later doesn't affect yours.</div>
      <select className="input" value={pick} onChange={(e) => setPick(e.target.value)} aria-label="Provider to copy">
        <option value="">Choose one of your providers…</option>
        {mine.map((p) => (
          <option key={p.id} value={p.id}>
            {p.name} · {p.models.length} models{p.keyHint ? ` · key ${p.keyHint}` : ''}
          </option>
        ))}
      </select>
      <div className="dialog-actions">
        <button className="btn btn-ghost" onClick={onClose}>
          Cancel
        </button>
        <button className="btn btn-primary" disabled={!pick} onClick={() => void copy()}>
          Copy
        </button>
      </div>
    </Modal>
  );
}

function DeleteDialog({ user, onClose, onDeleted }: { user: AdminUserDTO; onClose: () => void; onDeleted: () => void }) {
  const qc = useQueryClient();
  const [confirm, setConfirm] = useState('');
  const del = async () => {
    try {
      await api.del(`/api/admin/users/${user.id}`, { confirm });
      await qc.invalidateQueries({ queryKey: usersKey });
      toast(`${user.name} deleted`);
      onDeleted();
    } catch (e) {
      toast(errorText(e), 'error');
    }
  };
  return (
    <Modal open onClose={onClose} label="Delete person">
      <div className="dialog-title">Delete {user.name}?</div>
      <div className="dialog-body">
        This deletes their account, their {user.projectCount} {user.projectCount === 1 ? 'project' : 'projects'} with all conversations and memory, and their provider keys. It can't be undone. Type{' '}
        <b>{user.email}</b> to confirm.
      </div>
      <input className="input" value={confirm} onChange={(e) => setConfirm(e.target.value)} aria-label="Confirm email" autoFocus />
      <div className="dialog-actions">
        <button className="btn btn-ghost" onClick={onClose}>
          Cancel
        </button>
        <button className="btn btn-primary" disabled={confirm.trim().toLowerCase() !== user.email} onClick={() => void del()}>
          Delete
        </button>
      </div>
    </Modal>
  );
}
